import { isOlderBrief } from '../../utils/siteBriefFreshness';
import { subscribeBrief, reconciliationDelay, type BriefSnapshot } from '../../realtime';
import { useCallback, useEffect, useRef, useState } from 'react';
import { summaryReviewCopy, type SummaryAction, type SummaryEditDraft } from '../../utils/siteBriefSummaryReview';
import { SITE_BRIEF_STATUS_LABELS } from '../../utils/siteBriefStatusLabels';
import {
  stopSiteBriefTask,
  fetchSessionById,
  fetchSessionSiteBriefTask,
  fetchSiteBriefById,
  fetchSiteBriefRenderedSite,
  fetchSiteBriefSiteStructure,
  getPendingSiteBriefChatRequest,
  sendSiteBriefChatRequest,
  sendSiteBriefBuildRequest,
  SiteBuilderRequestError,
} from '../../api';
import type {
  GetSessionResponse,
  RenderedSiteType,
  SiteBriefChatMetadata,
  SiteBriefConversationReceipt,
  SiteBriefStatusType,
  SiteBriefTaskType,
  SiteBriefTaskResponse,
  SiteBriefType,
  SiteStructureType,
} from '../../api.types';

export type SiteBriefWorkspace = {
  session: GetSessionResponse['session'];
  metadata: SiteBriefChatMetadata;
  task: SiteBriefTaskType | null;
  brief: SiteBriefType | null;
  structure: SiteStructureType | null;
  creation: RenderedSiteType | null;
  canManage: boolean;
};

export type SiteBriefInitialSubmission = {
  sessionId: string;
  messageId: string;
  pending: boolean;
  error: string | null;
  receipt?: SiteBriefConversationReceipt | null;
};

export const isBriefTaskPending = (task: SiteBriefTaskType | null | undefined): boolean =>
  task?.state === 'queued' || task?.state === 'running' || task?.state === 'cancelling';

export type SiteBriefAutopilotStepType =
  | 'suggest_requirements'
  | 'build_site_structure'
  | 'generate_site_core'
  | 'fulfill_vibe_blocks';

// The build steps "Build a site" runs one after another, in order.
export const SITE_BRIEF_AUTOPILOT_STEPS: Array<{ operation: SiteBriefAutopilotStepType; label: string }> = [
  { operation: 'suggest_requirements', label: SITE_BRIEF_STATUS_LABELS.suggest_requirements },
  { operation: 'build_site_structure', label: SITE_BRIEF_STATUS_LABELS.build_site_structure },
  { operation: 'generate_site_core', label: SITE_BRIEF_STATUS_LABELS.generate_site_core },
  { operation: 'fulfill_vibe_blocks', label: SITE_BRIEF_STATUS_LABELS.fulfill_vibe_blocks },
];

// The step that runs next from each brief status; a fulfilled brief has nothing left to run.
export const SITE_BRIEF_AUTOPILOT_NEXT_STEP: Partial<Record<SiteBriefStatusType, SiteBriefAutopilotStepType>> = {
  gather_requirements: 'suggest_requirements',
  suggest_requirements: 'build_site_structure',
  build_site_structure: 'generate_site_core',
  generate_site_core: 'fulfill_vibe_blocks',
};

type SummaryFollowup = { kind: 'build' } | { kind: 'message'; message: string };
type SavedSummaryAction = { requestId: string; taskId?: string; followup?: SummaryFollowup };

function readSummaryStorage<T>(key: string): T | null {
  try {
    return JSON.parse(sessionStorage.getItem(key) || 'null') as T | null;
  } catch {
    return null;
  }
}

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Something went wrong. Please try again.';

export default function useSiteBriefWorkspace(token: string, sessionId: string, initialSubmission: SiteBriefInitialSubmission | null = null) {
  const [workspace, setWorkspace] = useState<SiteBriefWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState<string | null>(initialSubmission?.error ?? null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [pendingMessage, setPendingMessage] = useState<string | null>(
    () =>
      sessionStorage.getItem(`site-builder-submitted:${sessionId}`) ||
      getPendingSiteBriefChatRequest(sessionId)?.message ||
      null,
  );
  const summaryDraftKey = `site-brief-summary-draft:${sessionId}`;
  const summaryActionKey = `site-brief-summary-action:${sessionId}`;
  const [summaryDraft, setSummaryDraft] = useState<SummaryEditDraft | null>(() => {
    const value = readSummaryStorage<SummaryEditDraft>(summaryDraftKey);
    return typeof value?.before === 'string' && typeof value.after === 'string' ? value : null;
  });
  const summaryDraftRef = useRef(summaryDraft);
  const savedSummaryAction = useRef<SavedSummaryAction | null>(readSummaryStorage(summaryActionKey));
  const followSummaryAction = useRef<(action: SummaryFollowup) => Promise<unknown>>();
  const alive = useRef(true);
  const mutationLock = useRef(false);
  const generation = useRef(0);
  const taskId = useRef<string>();
  const snapshot = useRef<SiteBriefWorkspace | null>(null);
  const refreshLock = useRef(false);
  const refreshAgain = useRef(false);
  const initialMessageId = useRef(initialSubmission?.messageId);
  const [buildError, setBuildError] = useState<string | null>(null);
  const autopilot = workspace?.task?.operation === 'build_site' && isBriefTaskPending(workspace.task);
  const autopilotError = buildError || (workspace?.task?.operation === 'build_site' && workspace.task.state === 'failed' ? workspace.task.error : null);

  const pendingSnapshot = useRef<BriefSnapshot>();
  const progressRevision = useRef(0);
  const olderProgress = useCallback((progress: SiteBriefTaskResponse) => isOlderBrief(
    { ...progress.site_brief, task: progress.task },
    snapshot.current ? { ...snapshot.current.metadata, task: snapshot.current.task } : null,
  ), []);
  const completeProgress = useCallback((progress: SiteBriefTaskResponse, messages: GetSessionResponse['session']['messages']) => {
    const savedAction = savedSummaryAction.current;
    if (savedAction && (savedAction.taskId === progress.task?.id || savedAction.requestId === progress.task?.user_message_id)
      && progress.task && !isBriefTaskPending(progress.task)) {
      savedSummaryAction.current = null;
      sessionStorage.removeItem(summaryActionKey);
      if (progress.task?.state === 'completed') {
        summaryDraftRef.current = null;
        setSummaryDraft(null);
        sessionStorage.removeItem(summaryDraftKey);
        if (savedAction.followup) {
          const action = savedAction.followup;
          const actionGeneration = generation.current;
          setTimeout(() => {
            if (alive.current && generation.current === actionGeneration) {
              void followSummaryAction.current?.(action);
            }
          }, 0);
        }
      }
    }
    if (initialMessageId.current && messages.some((message) => message.id === initialMessageId.current)) {
      setError(null);
      initialMessageId.current = undefined;
    }
    const pending = getPendingSiteBriefChatRequest(sessionId);
    if (
      pending &&
      (progress.task?.user_message_id === pending.client_request_id ||
        messages.some((message) => message.id === pending.client_request_id))
    ) {
      sessionStorage.removeItem(`site-brief-chat:${sessionId}`);
      setError(null);
    }
    if (!isBriefTaskPending(progress.task)) {
      taskId.current = undefined;
    }
    if (progress.task?.state === 'completed' || progress.task?.state === 'cancelled') {
      setPendingMessage(null);
      sessionStorage.removeItem(`site-builder-submitted:${sessionId}`);
    }
  }, [sessionId, summaryActionKey, summaryDraftKey]);
  const applyProgress = useCallback((progress: SiteBriefTaskResponse, supplied?: BriefSnapshot) => {
    const current = snapshot.current;
    if (!current || olderProgress(progress)) {
      return;
    }
    const detail = supplied?.detail?.site_brief;
    const messages = [...current.session.messages];
    const userMessageId = progress.task?.user_message_id;
    const pending = getPendingSiteBriefChatRequest(sessionId);
    const submitted = pending && pending.client_request_id === userMessageId ? pending.message
      : taskId.current === progress.task?.id || initialMessageId.current === userMessageId
        ? sessionStorage.getItem(`site-builder-submitted:${sessionId}`) : null;
    // Keep the locally submitted question before its pushed reply until history reconciles it.
    if (progress.task && userMessageId && submitted && !messages.some(message => message.id === userMessageId)) {
      messages.push({ id: userMessageId, role: 'user', content: submitted,
        timestamp: progress.task.created_at, duration: 0, is_liked: false, is_disliked: false });
    }
    if (progress.lastReply && !messages.some(message => message.id === progress.lastReply?.id)) {
      messages.push(progress.lastReply);
    }
    const next = {
      ...current,
      session: { ...current.session, messages },
      metadata: progress.site_brief,
      task: progress.task,
      brief: detail && !isOlderBrief(detail, current.brief) ? detail : current.brief,
      canManage: supplied ? Boolean(supplied.detail) : current.canManage,
    };
    snapshot.current = next;
    setWorkspace(next);
    completeProgress(progress, messages);
  }, [olderProgress, completeProgress, sessionId]);
  const refresh = useCallback(async (incoming?: BriefSnapshot) => {
    if (!alive.current) {
      return;
    }
    if (incoming?.progress) {
      if (olderProgress(incoming.progress)) {
        return;
      }
      const previous = pendingSnapshot.current?.progress;
      if (previous && isOlderBrief({ ...incoming.progress.site_brief, task: incoming.progress.task },
        { ...previous.site_brief, task: previous.task })) {
        return;
      }
      pendingSnapshot.current = incoming;
      if (!mutationLock.current) {
        progressRevision.current += 1;
        applyProgress(incoming.progress, incoming);
      }
    }
    if (refreshLock.current || mutationLock.current) {
      refreshAgain.current = true;
      return;
    }
    refreshLock.current = true;
    refreshAgain.current = false;
    const currentGeneration = generation.current;
    const revision = progressRevision.current;
    const isCurrent = () => alive.current && generation.current === currentGeneration && progressRevision.current === revision;
    try {
      let supplied = pendingSnapshot.current;
      pendingSnapshot.current = undefined;
      if (supplied?.progress && olderProgress(supplied.progress)) {
        supplied = undefined;
      }
      const progress = supplied?.progress ?? await fetchSessionSiteBriefTask(token, sessionId, taskId.current);
      if (!isCurrent() || olderProgress(progress)) {
        return;
      }
      applyProgress(progress, supplied);
      // Read history after the receipt so a completed task's reply is already committed.
      const response = await fetchSessionById(token, sessionId);
      if (response.session.type !== 'vibe_site') {
        throw new Error('This conversation is not a site brief. Choose a site conversation from the sidebar.');
      }
      let canManage = supplied ? Boolean(supplied.detail) : snapshot.current?.canManage ?? true;
      let brief: SiteBriefType | null = null;
      let structure = snapshot.current?.structure ?? null;
      let creation = snapshot.current?.creation ?? null;
      if (canManage) {
        try {
          brief = supplied?.detail?.site_brief ?? await fetchSiteBriefById(token, progress.site_brief.id);
        } catch (failure) {
          if (failure instanceof SiteBuilderRequestError && failure.status === 403) {
            canManage = false;
          } else {
            throw failure;
          }
        }
      }
      if (canManage && brief) {
        const optionalRead = async <T>(read: () => Promise<T>): Promise<T | null> => {
          try {
            return await read();
          } catch (failure) {
            if (failure instanceof SiteBuilderRequestError && failure.status === 404) {
              return null;
            }
            throw failure;
          }
        };
        const hasStructure = ['build_site_structure', 'generate_site_core', 'fulfill_vibe_blocks'].includes(
          brief.status,
        );
        const hasCreation =
          hasStructure &&
          (brief.status !== 'build_site_structure' || progress.task?.operation === 'generate_site_core');
        [structure, creation] = await Promise.all([
          hasStructure ? optionalRead(() => fetchSiteBriefSiteStructure(token, brief.id)) : Promise.resolve(null),
          hasCreation ? optionalRead(() => fetchSiteBriefRenderedSite(token, brief.id)) : Promise.resolve(null),
        ]);
      }
      if (!isCurrent() || olderProgress(progress)) {
        return;
      }
      if (progress.task?.operation === 'build_site') {
        sessionStorage.removeItem(`site-builder-build-request:${sessionId}`);
        sessionStorage.removeItem(`site-brief-autopilot:${sessionId}`);
        if (isBriefTaskPending(progress.task) || progress.task.state === 'completed') setBuildError(null);
      }
      const next: SiteBriefWorkspace = {
        session: response.session,
        metadata: progress.site_brief,
        task: progress.task,
        brief,
        structure,
        creation,
        canManage,
      };
      if (next.brief && isOlderBrief(next.brief, snapshot.current?.brief)) {
        next.brief = snapshot.current?.brief ?? null;
      }
      snapshot.current = next;
      setWorkspace(next);
      completeProgress(progress, response.session.messages);
      setSyncError(null);
    } catch (failure) {
      if (isCurrent()) {
        setSyncError(errorMessage(failure));
      }
    } finally {
      refreshLock.current = false;
      if (isCurrent()) {
        setLoading(false);
      }
      if (alive.current && refreshAgain.current) {
        queueMicrotask(() => { void refresh(); });
      }
    }
  }, [sessionId, token, olderProgress, applyProgress, completeProgress]);

  useEffect(() => {
    if (initialSubmission && !initialSubmission.pending) {
      const messageSaved = snapshot.current?.session.messages.some((message) => message.id === initialSubmission.messageId);
      setError(messageSaved ? null : initialSubmission.error);
      if (initialSubmission.error && !messageSaved && !getPendingSiteBriefChatRequest(sessionId)) {
        setPendingMessage(null);
      }
      const receipt = initialSubmission.receipt;
      if (receipt) {
        taskId.current = receipt.task.id;
        const current = snapshot.current;
        // A poll may already have observed completion before the admission response arrived.
        if (current && current.metadata.version <= receipt.site_brief.version
          && !(current.task?.id === receipt.task.id && !isBriefTaskPending(current.task))) {
          generation.current += 1;
          const next = { ...current, metadata: { ...current.metadata, ...receipt.site_brief }, task: receipt.task };
          snapshot.current = next;
          setWorkspace(next);
        }
      }
      void refresh();
    }
  }, [initialSubmission, refresh, sessionId]);

  useEffect(() => {
    alive.current = true;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      clearTimeout(timer);
      await refresh();
      if (alive.current) {
        const current = snapshot.current;
        const pending = isBriefTaskPending(current?.task) || current?.metadata.active_task_id;
        clearTimeout(timer);
        timer = setTimeout(
          () => {
            void poll();
          },
          reconciliationDelay(pending ? 1800 : 7000),
        );
      }
    };
    void poll();
    const unsubscribe = subscribeBrief({ sessionId }, () => { void poll(); }, incoming => { void refresh(incoming); });
    const onFocus = () => { void poll(); };
    window.addEventListener('focus', onFocus);
    return () => {
      unsubscribe();
      window.removeEventListener('focus', onFocus);
      alive.current = false;
      clearTimeout(timer);
    };
  }, [refresh, sessionId]);

  const admit = async (message: string, action?: SummaryAction, followup?: SummaryFollowup): Promise<boolean> => {
    const current = snapshot.current;
    if (
      !current ||
      mutationLock.current ||
      isBriefTaskPending(current.task) ||
      current.metadata.active_task_id ||
      current.metadata.status !== 'gather_requirements'
    ) {
      return false;
    }
    mutationLock.current = true;
    generation.current += 1;
    setMutating(true);
    setError(null);
    if (action?.summary_action !== 'edit') {
      setPendingMessage(message);
      sessionStorage.setItem(`site-builder-submitted:${sessionId}`, message);
    }
    let accepted = false;
    const requestId = getPendingSiteBriefChatRequest(sessionId)?.client_request_id ?? crypto.randomUUID();
    if (action?.summary_action === 'edit') {
      savedSummaryAction.current = { requestId, ...(followup ? { followup } : {}) };
      sessionStorage.setItem(summaryActionKey, JSON.stringify(savedSummaryAction.current));
    }
    try {
      const receipt = await sendSiteBriefChatRequest(
        token,
        sessionId,
        message,
        current.metadata.version,
        requestId,
        action,
      );
      if (alive.current) {
        const next = { ...current, metadata: { ...current.metadata, ...receipt.site_brief }, task: receipt.task };
        snapshot.current = next;
        setWorkspace(next);
        taskId.current = receipt.task.id;
      }
      if (action?.summary_action === 'edit') {
        savedSummaryAction.current = { requestId, taskId: receipt.task.id, ...(followup ? { followup } : {}) };
        sessionStorage.setItem(summaryActionKey, JSON.stringify(savedSummaryAction.current));
      }
      accepted = true;
    } catch (failure) {
      if (alive.current) {
        setError(errorMessage(failure));
        if (!getPendingSiteBriefChatRequest(sessionId)) {
          if (action?.summary_action === 'edit') {
            savedSummaryAction.current = null;
            sessionStorage.removeItem(summaryActionKey);
          }
          setPendingMessage(null);
          sessionStorage.removeItem(`site-builder-submitted:${sessionId}`);
        }
      }
    } finally {
      mutationLock.current = false;
      if (alive.current) {
        setMutating(false);
        void refresh();
      }
    }
    return accepted;
  };

  const startBuild = async (): Promise<void> => {
    const current = snapshot.current;
    if (!current || mutationLock.current || isBriefTaskPending(current.task) || current.metadata.active_task_id
      || (!current.metadata.can_build && !current.metadata.can_retry) || getPendingSiteBriefChatRequest(sessionId)) {
      return;
    }
    mutationLock.current = true;
    generation.current += 1;
    setBuilding(true);
    setMutating(true);
    setBuildError(null);
    try {
      const receipt = await sendSiteBriefBuildRequest(token, sessionId, current.metadata.id, current.metadata.version,
        current.metadata.can_retry ? current.task?.id : undefined);
      if (alive.current) {
        const next = { ...current, metadata: { ...current.metadata, ...receipt.site_brief }, task: receipt.task };
        snapshot.current = next;
        setWorkspace(next);
        taskId.current = receipt.task.id;
      }
    } catch (failure) {
      if (alive.current) setBuildError(errorMessage(failure));
    } finally {
      mutationLock.current = false;
      if (alive.current) {
        setBuilding(false);
        setMutating(false);
        void refresh();
      }
    }
  };

  const changeSummary = (after: string) => {
    const before = summaryDraftRef.current?.before ?? snapshot.current?.metadata.presentation?.summary ?? '';
    const value = after === before ? null : { before, after };
    summaryDraftRef.current = value;
    setSummaryDraft(value);
    if (value) {
      sessionStorage.setItem(summaryDraftKey, JSON.stringify(value));
    } else {
      sessionStorage.removeItem(summaryDraftKey);
    }
  };
  const discardSummary = () => {
    summaryDraftRef.current = null;
    setSummaryDraft(null);
    sessionStorage.removeItem(summaryDraftKey);
    setError(null);
  };
  const saveSummary = async (followup?: SummaryFollowup): Promise<boolean> => {
    const edit = summaryDraftRef.current;
    if (!edit || !edit.after.trim()) {
      return false;
    }
    return admit(edit.after, { summary_action: 'edit', summary_before: edit.before }, followup);
  };
  const send = async (message: string): Promise<boolean> => {
    if (summaryDraftRef.current) {
      await saveSummary({ kind: 'message', message });
      // Keep the chat draft until the follow-up message itself is admitted, including if saving fails.
      return false;
    }
    return admit(message);
  };
  const buildSite = async (): Promise<void> => {
    const current = snapshot.current;
    // Before Evo finishes the interview, the first press opens the brief instead of building.
    if (current?.metadata.status === 'gather_requirements' && !current.metadata.is_gather_requirements_done) {
      if (!current.metadata.presentation?.summary.trim()) {
        return;
      }
      const copy = summaryReviewCopy(current.metadata.presentation.language ?? current.session.language ?? 'en');
      await admit(copy.revealMessage, { summary_action: 'reveal' });
    } else if (summaryDraftRef.current) {
      await saveSummary({ kind: 'build' });
    } else {
      await startBuild();
    }
  };
  followSummaryAction.current = async (action) => {
    if (action.kind === 'build') {
      return startBuild();
    }
    // A follow-up saved before the refine action was removed has nothing left to send.
    return action.kind === 'message' ? admit(action.message) : undefined;
  };
  const stopTask = async (): Promise<void> => {
    const current = snapshot.current;
    if (!current?.task || mutationLock.current) return;
    mutationLock.current = true;
    generation.current += 1;
    savedSummaryAction.current = null;
    sessionStorage.removeItem(summaryActionKey);
    sessionStorage.removeItem(`site-brief-autopilot:${sessionId}`);
    setMutating(true);
    try {
      await stopSiteBriefTask(token, current.metadata.id, current.task.id, current.metadata.version);
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      mutationLock.current = false;
      setMutating(false);
      void refresh();
    }
  };
  const resumeAutopilot = (): void => { void startBuild(); };

  return {
    workspace,
    loading,
    mutating,
    building,
    error,
    syncError,
    pendingMessage,
    send,
    buildSite,
    summaryDraft,
    changeSummary,
    discardSummary,
    saveSummary,
    refresh,
    autopilot,
    autopilotError,
    resumeAutopilot,
    stopTask,
  };
}
